import AssetTypeServices from '@Services/AssetType.services.ts';
import { AssetDocument, AssetType } from '@Types/Asset.types';
import { AssetFormFields } from '@Types/Form.types';
import {
  ASSET_FORM_DATA_KEYS,
  ASSET_TYPES_WITH_REQUIRED_SERIAL_NUMBER,
  DROPDOWN_DEFAULT,
  FIELD_NAMES,
} from '@Utils/constants';
import { RegisterOptions } from 'react-hook-form';
import { getOnlyDateString } from './date.utils.ts';

export const formatRequired = (label: string, required: boolean = true) => {
  if (required) {
    return `${label} *`;
  }
  return label;
};

export const formatCreateValues = (submitInfo: AssetFormFields) => {
  const formattedData: Partial<AssetFormFields> = { ...submitInfo };
  delete formattedData.assetTemplate;

  const { documents } = submitInfo;
  delete formattedData.documents;

  const prunedSubmitInfo = Object.fromEntries(Object.entries(formattedData).filter(([, value]) => value !== ''));

  const formData = new FormData();
  formData.append(ASSET_FORM_DATA_KEYS.FIELDS, JSON.stringify(prunedSubmitInfo));
  if (documents) {
    documents.forEach((doc: File | AssetDocument) => {
      if (doc instanceof File) {
        formData.append(ASSET_FORM_DATA_KEYS.DOCUMENTS, doc);
      }
    });
  }

  return formData;
};

export const formatEditValues = (submitInfo: AssetFormFields) => {
  const formData = new FormData();

  const formattedData: Partial<AssetFormFields> = { ...submitInfo };
  delete formattedData.assetTemplate;

  const prunedSubmitInfo = Object.fromEntries(
    Object.entries(formattedData).filter(([, value]) => value !== undefined && value !== null),
  );

  if (prunedSubmitInfo.deletedDocuments && (prunedSubmitInfo.deletedDocuments as AssetDocument[]).length === 0) {
    delete prunedSubmitInfo.deletedDocuments;
  }

  if (!prunedSubmitInfo.purchaseDate) {
    delete prunedSubmitInfo.purchaseDate;
  } else {
    getOnlyDateString(prunedSubmitInfo.purchaseDate as Date);
  }

  const { documents } = prunedSubmitInfo;

  if (documents) {
    const newDocuments = (documents as AssetDocument[]).filter((doc: File | AssetDocument) => doc instanceof File);

    newDocuments.forEach((doc: File | AssetDocument) => {
      if (doc instanceof File) {
        formData.append(ASSET_FORM_DATA_KEYS.DOCUMENTS, doc);
      }
    });

    submitInfo.documents = (documents as AssetDocument[]).filter((doc: File | AssetDocument) => !(doc instanceof File));
  }

  formData.append(ASSET_FORM_DATA_KEYS.FIELDS, JSON.stringify(prunedSubmitInfo));

  return formData;
};

export const getDocumentName = (doc: File | AssetDocument) => (doc instanceof File ? doc.name : doc.filename);

export function removeRequired<T extends RegisterOptions>(rules: T): Omit<T, 'required'> {
  const restRules = { ...rules };
  delete restRules.required;

  if (!restRules.validate || typeof restRules.validate === 'function') {
    return restRules;
  }

  const restValidate = { ...restRules.validate };
  delete restValidate.required;

  return {
    ...restRules,
    validate: Object.keys(restValidate).length > 0 ? restValidate : undefined,
  };
}

export const removeDefaults = (data: AssetFormFields) => {
  const cleanData: Partial<AssetFormFields> = { ...data };

  const fieldsToCheck: (keyof AssetFormFields)[] = [
    FIELD_NAMES.ASSET_TYPE,
    FIELD_NAMES.ASSET_STATUS,
    FIELD_NAMES.OFFICE_ID,
    FIELD_NAMES.TAX_STATUS,
  ];

  fieldsToCheck.forEach((field) => {
    if (!cleanData[field] || cleanData[field] === DROPDOWN_DEFAULT.value) {
      delete cleanData[field];
    }
  });

  return cleanData;
};

export const formatCreateAssetTemplateValues = (submitInfo: AssetFormFields) => {
  const formattedData: Partial<AssetFormFields> = removeDefaults({ ...submitInfo });
  delete formattedData.assetTemplate;
  delete formattedData.deletedDocuments;
  delete formattedData.documents;

  const prunedSubmitInfo = Object.fromEntries(
    Object.entries(formattedData).filter(([, value]) => value !== undefined && value !== null),
  );

  const formData = new FormData();
  formData.append(ASSET_FORM_DATA_KEYS.FIELDS, JSON.stringify(prunedSubmitInfo));

  return formData;
};

export const isSerialNumberRequired = async (assetTypeId: string): Promise<boolean> => {
  const assetTypes: AssetType[] = (await AssetTypeServices.getAssetTypes(null, false)).data.docs;
  const selected = assetTypes.find((assetType) => assetType.id === assetTypeId);
  return ASSET_TYPES_WITH_REQUIRED_SERIAL_NUMBER.includes(selected?.description ?? '');
};

export const isAssetDescriptionRequired = async (assetTypeId: string): Promise<boolean> => {
  // asset description is required when serial number is not

  const requireSerialNumber = await isSerialNumberRequired(assetTypeId);
  return !requireSerialNumber;
};
